import {
  BillInfo,
  EntityType,
  OrganizationTranslations,
  PaymentMethodDetails,
  Prisma,
  prisma,
} from "client";
import { setNewOrganizationWithDefaultTemplates } from "services/defaultTemplate";

export enum OrganizerFilters {
  ALL = "All",
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

const isObjectId = (value: string) => /^[a-f\d]{24}$/i.test(value);

export const normalizeOrganizationSlug = (slug: string) =>
  slug.trim().toLowerCase();

const organizationTranslationsInclude = (lang?: string) =>
  lang
    ? {
        where: {
          language: {
            code: lang,
          },
        },
      }
    : true;

//GET
export const getOrganizationById = async (
  idOrSlug: string,
  lang?: string,
  whiteLabelId?: string
) => {
  try {
    if (isObjectId(idOrSlug)) {
      return await prisma.organization.findUnique({
        where: { id: idOrSlug },
        include: {
          translations: organizationTranslationsInclude(lang),
          whiteLabel: true,
        },
      });
    }

    return await getOrganizationBySlug(idOrSlug, whiteLabelId, lang);
  } catch (error) {
    console.error("Error fetching organization by ID:", error);
    throw error;
  }
};
export const getOrganizationOwner = async (organizationId: string) => {
  try {
    const owner = await prisma.user.findFirst({
      where: {
        organizations: {
          some: {
            id: organizationId,
          },
        },
        roleV2: {
          name: "ORGANIZATION_OWNER",
        },
      },
      include: {
        roleV2: true,
      },
    });
    return owner;
  } catch (error) {
    console.error("Error fetching organization owner:", error);
    throw error;
  }
};
export const getOrganizationBySlug = async (
  slug: string,
  whiteLabelId?: string,
  lang?: string
) => {
  try {
    if (!whiteLabelId) {
      return null;
    }

    const normalizedSlug = normalizeOrganizationSlug(slug);

    return await prisma.organization.findUnique({
      where: {
        slug_whiteLabelId: {
          slug: normalizedSlug,
          whiteLabelId,
        },
      },
      include: {
        translations: organizationTranslationsInclude(lang),
        whiteLabel: true,
      },
    });
  } catch (error) {
    console.error("Error fetching organization by slug:", error);
    throw error;
  }
};

export const isOrganizationSlugTaken = async (
  slug: string,
  whiteLabelId: string
) => {
  const normalizedSlug = normalizeOrganizationSlug(slug);
  const organization = await prisma.organization.findUnique({
    where: {
      slug_whiteLabelId: {
        slug: normalizedSlug,
        whiteLabelId,
      },
    },
    select: { id: true },
  });
  return Boolean(organization);
};

export const getOrganizationsBySlugPrefix = async (
  baseSlug: string,
  whiteLabelId?: string
) => {
  try {
    const organizations = await prisma.organization.findMany({
      where: {
        slug: {
          startsWith: baseSlug,
          mode: "insensitive",
        },
        ...(whiteLabelId ? { whiteLabelId } : {}),
      },
      select: {
        slug: true,
      },
    });
    return organizations;
  } catch (error) {
    console.error("Error fetching organizations by slug prefix:", error);
    throw error;
  }
};
export const getAllOrganizations = async () => {
  const organizations = await prisma.organization.findMany();
  return organizations;
};

export const getOrganizationByWhitLabelId = async (
  whiteLabelId: string,
  lang?: string,
  searchStr?: string,
  filter?: OrganizerFilters
) => {
  const activeFilter =
    filter === OrganizerFilters.ACTIVE
      ? true
      : filter === OrganizerFilters.INACTIVE
      ? false
      : undefined; // undefined for "all"

  const organizers = await prisma.organization.findMany({
    where: {
      whiteLabelId: whiteLabelId,
      translations: {
        some: {
          name: {
            contains: searchStr,
            mode: "insensitive",
          },
        },
      },
      ...(activeFilter !== undefined && { active: activeFilter }), // Add active conditionally
    },
    include: {
      whiteLabel: {
        select: {
          subdomain: true,
          customDomain: true,
        },
      },
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
  return organizers.map((o) => ({
    ...o,
    ...o.translations?.[0],
    id: o.id,
  }));
};

//POST
export const createOrganization = async (
  data: Prisma.OrganizationCreateInput,
  translations: OrganizationTranslations[],
  createById: string,
  whiteLabelId: string
) => {
  try {
    const normalizedSlug = data.slug
      ? normalizeOrganizationSlug(data.slug)
      : undefined;

    if (normalizedSlug) {
      const slugTaken = await isOrganizationSlugTaken(
        normalizedSlug,
        whiteLabelId
      );
      if (slugTaken) {
        throw new Error(
          "An organization with this slug already exists in this white-label. Please choose a different slug."
        );
      }
    }

    const organization = await prisma.organization.create({
      data: {
        ...data,
        ...(normalizedSlug ? { slug: normalizedSlug } : {}),
        createdBy: {
          connect: {
            id: createById,
          },
        },
        whiteLabel: {
          connect: {
            id: whiteLabelId,
          },
        },
        translations: {
          createMany: {
            data: translations,
          },
        },
      },
    });
    await setNewOrganizationWithDefaultTemplates(createById, organization.id);
    return organization;
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

//UPDATE
export const updateOrganization = async (
  id: string,
  slug: string,
  organizationPayload: Prisma.OrganizationCreateInput,
  translations?: OrganizationTranslations[]
) => {
  const organizationTranslations = {
    translations: {
      updateMany: translations?.map((t) => ({
        where: {
          languageId: t.languageId,
        },
        data: {
          name: t.name,
          companyName: t.companyName,
        },
      })),
    },
  };
  try {
    const current = await prisma.organization.findUnique({
      where: { id },
      select: { id: true, slug: true, whiteLabelId: true },
    });
    if (!current) {
      throw new Error("Organization not found");
    }

    const payload = organizationPayload as any;
    const desiredSlugRaw = (slug ?? payload?.slug) as string | undefined;
    const nextSlug = desiredSlugRaw
      ? normalizeOrganizationSlug(desiredSlugRaw)
      : undefined;
    if (nextSlug && nextSlug !== current.slug) {
      const existing = await prisma.organization.findUnique({
        where: {
          slug_whiteLabelId: {
            slug: nextSlug,
            whiteLabelId: current.whiteLabelId,
          },
        },
        select: { id: true },
      });
      if (existing && existing.id !== id) {
        throw new Error(
          "An organization with this slug already exists in this white-label. Please choose a different slug."
        );
      }
    }

    // Prevent unintended slug updates bypassing the check above.
    // We always apply slug via `nextSlug` only.
    const { slug: _ignoredSlug, ...organizationPayloadWithoutSlug } = payload ?? {};

    const event = await prisma.organization.update({
      where: {
        id,
      },
      data: {
        ...organizationPayloadWithoutSlug,
        ...(nextSlug ? { slug: nextSlug } : {}),
        ...organizationTranslations,
      },
    });
    return event;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const getOrganizationEvents = async (
  organizationId: string,
  lang?: string
) => {
  try {
    const events = await prisma.event.findMany({
      where: {
        organizationIds: {
          has: organizationId,
        },
      },
      include: {
        translations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
      },
    });
    return events.map((e) => ({
      ...e,
      ...e.translations?.[0],
      id: e.id,
    }));
  } catch (error) {
    console.error("Error fetching organization events:", error);
    throw error;
  }
};
export const getOrganizationEventsCount = async (organizationId: string) => {
  const eventsCount = await prisma.event.count({
    where: { organizationIds: { has: organizationId } },
  });
  return eventsCount;
};

export const getOrganizationEventById = async (
  organizationId: string,
  eventId: string
) => {
  const event = await prisma.event.findFirst({
    where: { id: eventId, organizations: { some: { id: organizationId } } },
  });
  return event;
};

export const getOrganizationByUserIdAndWhiteLabelId = async (
  userId: string,
  whiteLabelId?: string,
  organizationSlug?: string
) => {
  let whereClause: Prisma.OrganizationWhereInput = {
    team: {
      some: {
        id: userId,
      },
    },
  };
  if (whiteLabelId) {
    whereClause.whiteLabelId = whiteLabelId;
  } else if (organizationSlug) {
    whereClause.slug = organizationSlug;
  }
  const organization = await prisma.organization.findFirst({
    where: whereClause,
    include: {
      whiteLabel: true,
    },
  });
  return organization;
};

export const getOrganizationByEventIdAndUserId = async (
  eventId: string,
  userId: string
) => {
  const organization = await prisma.organization.findFirst({
    where: {
      events: {
        some: {
          id: eventId,
        },
      },
      teamIds: { has: userId },
    },
    select: {
      id: true,
      translations: {
        select: {
          name: true,
        },
      },
    },
  });
  return organization;
};

export const countOrganizationBoothsAndSeatsByUserId = async (
  organizationId: string,
  userId?: string
) => {
  // const boothsCount = await prisma.entity.count({
  //   where: {
  //     shape: {
  //       venue: {
  //         events: {
  //           some: {
  //             organizations: {
  //               some: {
  //                 id: organizationId,
  //                 ...(userId ? { teamIds: { has: userId } } : {}),
  //               },
  //             },
  //           },
  //         },
  //       },
  //     },
  //     type: EntityType.BOOTH,
  //   },
  // });
  // const seatsCount = await prisma.entity.count({
  //   where: {
  //     shape: {
  //       venue: {
  //         events: {
  //           some: {
  //             organizations: {
  //               some: {
  //                 id: organizationId,
  //                 ...(userId ? { teamIds: { has: userId } } : {}),
  //               },
  //             },
  //           },
  //         },
  //       },
  //     },
  //     type: EntityType.SEAT,
  //   },
  // });
  return { boothsCount: 0, seatsCount: 0 };
};

export const getOrganizationAppearance = async (organizationId: string) => {
  return prisma.organization.findUnique({
    where: { id: organizationId },
    select: {
      id: true,
      whiteLabelId: true,
      thumbnailImage: true as any,
      message404: true as any,
      branding: {
        select: {
          logo: true,
          favIcon: true,
          font: true,
          primaryColor: true,
        },
      },
    } as any,
  });
};

export const getWhiteLabelAppearanceWithPolicy = async (
  whiteLabelId: string
) => {
  return prisma.whiteLabel.findUnique({
    where: { id: whiteLabelId },
    select: {
      id: true,
      image: true,
      message404: true,
      allowOrgOverrideThumbnail: true,
      allowOrgOverrideMessage404: true,
      branding: {
        select: {
          logo: true,
          favIcon: true,
          font: true,
          primaryColor: true,
          allowOrgOverrideLogo: true,
          allowOrgOverrideFavIcon: true,
          allowOrgOverrideFont: true,
          allowOrgOverridePrimaryColor: true,
        },
      },
    } as any,
  });
};

export const addBillToOrganization = async (
  organizationId: string,
  bill: BillInfo,
  paymentDetails: PaymentMethodDetails
) => {
  const organization = await prisma.organization.findUnique({
    where: { id: organizationId },
  });
  if (!organization) {
    throw new Error("Organization not found");
  }
  const isBillExist = organization.billingHistory.find(
    (b) => b.invoiceId === bill.invoiceId
  );
  if (isBillExist) {
    throw new Error("Bill already exists");
  }
  organization.billingHistory.push(bill);
  await prisma.organization.update({
    where: { id: organizationId },
    data: {
      billingHistory: {
        push: bill,
      },
      PaymentMethodDetails: {
        set: {
          cardNumber: paymentDetails.cardNumber,
          expireDate: paymentDetails.expireDate,
          paymentMethod: paymentDetails.paymentMethod,
        },
      },
    },
  });
  return organization;
};
